import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { Grid, FormControlLabel, Checkbox } from '@mui/material';
import { Card, Autocomplete, TextField, IconButton } from '@totalsoft/rocket-ui';
import { AssignmentOutlined as AssignmentOutlinedIcon } from '@mui/icons-material';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { get, set, over } from '@totalsoft/change-tracking-react';
import FormulaField from 'features/common/components/formula-field';
import { FormulaFieldContainer, FormulaFieldLabel } from 'components/common/Styles';
import { emptyString } from 'utils/constants';

export const LeasingExpenseTypeComponent = ({
  lens,
  validation,
  currencies,
  linkItem,
  loadLinkItems,
  taxRates,
  isDirty,
  disabled,
  onSave
}) => {
  const [t] = useTranslation();
  const { cvsChAmount, cvsChPercent } = useFieldDecimalPlacesConfig();

  const handleCurrencyChange = useCallback(
    value => {
      set(lens.currencyId)(value.id);
      set(lens.currencyCode)(value.code);
    },
    [lens.currencyId, lens.currencyCode]
  );

  const handleLinkItemChange = ({ id, name }) => {
    over(lens, prev => ({ ...prev, linkedItemId: id, linkedItemName: name }));
  };

  return (
    <Card
      icon={AssignmentOutlinedIcon}
      iconColor='primary'
      title={t('LeasingExpenseType.GeneralData')}
      actions={
        <IconButton
          type='save'
          fontSize='medium'
          color='transparent'
          tooltip={t('SaveChanges')}
          onClick={onSave}
          disabled={!isDirty || disabled}
        />
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required
            value={get(lens.expenseCode) || emptyString}
            onChange={event => set(lens.expenseCode)(event)}
            label={t('LeasingExpenseType.ExpenseCode')}
            disabled={disabled}
            error={!isValid(validation.expenseCode)}
            helperText={getErrors(validation.expenseCode)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required
            value={get(lens.expenseName) || emptyString}
            onChange={event => set(lens.expenseName)(event)}
            label={t('LeasingExpenseType.ExpenseName')}
            disabled={disabled}
            error={!isValid(validation.expenseName)}
            helperText={getErrors(validation.expenseName)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                checked={get(lens.mandatory) || false}
                onChange={event => set(lens.mandatory)(event.target.checked)}
                color='primary'
              />
            }
            label={t('LeasingExpenseType.Mandatory')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={disabled}
            value={linkItem}
            onChange={handleLinkItemChange}
            loadOptions={loadLinkItems}
            label={t('LeasingExpenseType.LinkedItem')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={disabled}
            required
            value={lens.vatId |> get}
            onChange={lens.vatId |> set}
            simpleValue
            valueKey='id'
            labelKey='name'
            label={t('LeasingExpenseType.TaxRate')}
            options={taxRates}
            error={!isValid(validation.vatId)}
            helperText={getErrors(validation.vatId)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={disabled}
            label={t('LeasingExpenseType.Currency')}
            labelKey='code'
            valueKey='id'
            value={{ id: lens.currencyId |> get, code: lens.currencyCode |> get }}
            onChange={handleCurrencyChange}
            options={currencies}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={disabled}
            fullWidth
            isNumeric
            decimalScale={cvsChAmount}
            value={get(lens.currencyValue) || emptyString}
            onChange={event => set(lens.currencyValue)(event)}
            label={t('LeasingExpenseType.CurrencyValue')}
            error={!isValid(validation.currencyValue)}
            helperText={getErrors(validation.currencyValue)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={disabled}
            fullWidth
            isNumeric
            decimalScale={cvsChPercent}
            value={get(lens.currencyPercent) || emptyString}
            onChange={event => set(lens.currencyPercent)(event)}
            label={t('LeasingExpenseType.CurrencyPercent')}
            error={!isValid(validation.currencyPercent)}
            helperText={getErrors(validation.currencyPercent)}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                checked={get(lens.isLogistic) || false}
                onChange={event => set(lens.isLogistic)(event.target.checked)}
                color='primary'
              />
            }
            label={t('LeasingExpenseType.IsLogistic')}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                checked={get(lens.isActive) || false}
                onChange={event => set(lens.isActive)(event.target.checked)}
                color='primary'
              />
            }
            label={t('LeasingExpenseType.IsActive')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={disabled}
            fullWidth
            isNumeric
            decimalScale={cvsChAmount}
            value={get(lens.minValue) || emptyString}
            onChange={event => set(lens.minValue)(event)}
            label={t('LeasingExpenseType.MinValue')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={disabled}
            fullWidth
            isNumeric
            decimalScale={cvsChAmount}
            value={get(lens.maxValue) || emptyString}
            onChange={event => set(lens.maxValue)(event)}
            label={t('LeasingExpenseType.MaxValue')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={disabled}
            fullWidth
            isNumeric
            decimalScale={0}
            value={get(lens.periodicity) || emptyString}
            onChange={event => set(lens.periodicity)(event)}
            label={t('LeasingExpenseType.Periodicity')}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <FormulaFieldLabel>{t('LeasingExpenseType.Formula')}</FormulaFieldLabel>
          <FormulaFieldContainer>
            <FormulaField value={lens.formula |> get} onChange={lens.formula |> set} disabled={disabled} />
          </FormulaFieldContainer>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            disabled={disabled}
            sx={{ mt: 2.5 }}
            control={
              <Checkbox
                checked={get(lens.autocalculate) || false}
                onChange={event => set(lens.autocalculate)(event.target.checked)}
                color='primary'
              />
            }
            label={t('LeasingExpenseType.Autocalculate')}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel
            disabled={disabled}
            sx={{ mt: 2.5 }}
            control={
              <Checkbox
                checked={get(lens.useFormula) || false}
                onChange={event => set(lens.useFormula)(event.target.checked)}
                color='primary'
              />
            }
            label={t('LeasingExpenseType.UseFormula')}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

LeasingExpenseTypeComponent.propTypes = {
  lens: PropTypes.object.isRequired,
  validation: PropTypes.object,
  currencies: PropTypes.array,
  linkItem: PropTypes.object,
  loadLinkItems: PropTypes.func.isRequired,
  taxRates: PropTypes.array,
  isDirty: PropTypes.bool,
  disabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired
};

export default LeasingExpenseTypeComponent;
